import type { ReactNode } from 'react';

import { useDashboardEditLock } from '@/features/dashboard/hooks/edit-lock/useDashboardEditLock';
import { ConfirmationDialog } from '@/features/dashboard/pages/Edit/components';

import { DashboardEditLockContext } from './DashboardEditLockContext';

/**
 * Provides cross-tab edit session locking for all Dashboard-related UI.
 *
 * Place this above any component that needs to guard dashboard mutations
 * (Edit mode, Add app, Create app from symbol selector, Add widget to dashboard).
 * The conflict dialog is rendered here so it is always in the tree regardless
 * of which component triggered the action.
 */
export const DashboardEditLockProvider = ({ children }: { children: ReactNode }) => {
  const {
    requestEdit,
    releaseEdit,
    isConflictDialogOpen,
    handleEndOtherSession,
    handleCancelEdit,
    setForceReleaseCallback,
    requestExclusiveAction
  } = useDashboardEditLock();

  return (
    <DashboardEditLockContext
      value={{
        requestEditLock: requestEdit,
        requestExclusiveAction,
        releaseEditLock: releaseEdit,
        setForceReleaseCallback
      }}
    >
      {children}
      <ConfirmationDialog
        cancelText='Go back'
        confirmText='Take over here'
        open={isConflictDialogOpen}
        text='Edit mode is already open in another tab. Only one active session is allowed.'
        title='Active session!'
        onCancel={handleCancelEdit}
        onConfirm={handleEndOtherSession}
      />
    </DashboardEditLockContext>
  );
};
